'use client';

import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import FilterDropdown from '@/components/shared/FilterDropdown';
import { H4 } from '@/components/shared/Typography';
import { C_Regexp } from '@/ts/constants/C_Regexp';

interface I_HistoryFiltersProps {
  variant: 'predictions' | 'pools';
  filterValue: string;
  sortValue: string;
  selectedTokens: string[];
  availableTokenSymbols?: string[];
  onFilterChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onTokensChange: (tokens: string[]) => void;
}

export function HistoryFilters({
  variant,
  filterValue,
  sortValue,
  selectedTokens,
  availableTokenSymbols,
  onFilterChange,
  onSortChange,
  onTokensChange
}: I_HistoryFiltersProps) {
  const t = useTranslations();
  const isPools = variant === 'pools';
  const isPredictions = variant === 'predictions';

  // Get content titleKey based on variant
  const contentTitleKey = useMemo(() => {
    if (variant === 'predictions') {
      return 'profile.sections.predictions.content.title';
    }
    if (variant === 'pools') {
      return 'profile.sections.pools.content.title';
    }
    return '';
  }, [variant]);

  const filterOptions = useMemo(() => {
    if (isPools) {
      return [
        { value: 'all', label: t('profile.filters.all') },
        { value: 'buy', label: t('profile.filters.buy') },
        { value: 'sell', label: t('profile.filters.sell') }
      ];
    }
    // For predictions - outcome filter (radio buttons)
    return [
      { value: 'all', label: t('profile.filters.all') },
      { value: 'won', label: t('profile.filters.won') },
      { value: 'lost', label: t('profile.filters.lost') },
      { value: 'ongoing', label: t('profile.filters.ongoing') },
      { value: 'canceled', label: t('profile.filters.canceled') }
    ];
  }, [isPools, t]);

  const sortOptions = [
    { value: 'newest', label: t('profile.sort.newest') },
    { value: 'oldest', label: t('profile.sort.oldest') }
  ];

  // Use dynamic token symbols from prediction history response
  const tokenOptions = useMemo(() => {
    if (
      isPredictions &&
      availableTokenSymbols &&
      availableTokenSymbols.length > 0
    ) {
      // Get unique token symbols (keep original format, e.g., "TxBTC")
      const uniqueSymbols = Array.from(
        new Set(availableTokenSymbols.filter(Boolean))
      ).sort();

      return uniqueSymbols.map(symbol => {
        // Remove "Tx" prefix from label for display, but keep original value
        const displayLabel = symbol.replace(C_Regexp.txPrefix, '');
        return {
          value: symbol, // Keep original tokenSymbol (e.g., "TxBTC") for API
          label: displayLabel // Display without "Tx" prefix (e.g., "BTC")
        };
      });
    }

    // Return empty array if no tokens available
    return [];
  }, [isPredictions, availableTokenSymbols]);

  return (
    <div>
      <div className="flex flex-col gap-4 mb-5">
        <H4>{t(contentTitleKey)}</H4>
        <div className="flex gap-4 justify-between">
          <FilterDropdown
            mode={isPools ? 'single' : 'multi'}
            options={filterOptions}
            value={filterValue}
            onValueChange={onFilterChange}
            label={t('profile.filters.filterBy')}
            triggerLabel="Filters"
            showIcon={true}
            showSelectedValue={false}
            className="w-full rounded-full px-4 py-2"
            checkboxOptions={isPredictions ? tokenOptions : undefined}
            checkboxValues={isPredictions ? selectedTokens : []}
            onCheckboxChange={isPredictions ? onTokensChange : undefined}
            checkboxLabel={isPredictions ? 'Token' : undefined}
          />
          <FilterDropdown
            mode="single"
            options={sortOptions}
            value={sortValue}
            onValueChange={onSortChange}
            label={t('profile.sort.sortBy')}
            triggerLabel="Sort"
            showIcon={false}
            showSelectedValue={true}
            className="w-full rounded-full px-4 py-2"
          />
        </div>
      </div>
    </div>
  );
}
